import { configureStore } from '@reduxjs/toolkit';
import contributorsReducer from './contributorsSlice';
import mobileMenuReducer from './mobileMenuSlice';
import authReducer from './authSlice';
import alertReducer, { showAlert } from './alertSlice';
import loadingReducer from './loadingSlice';
import statsReducer from './statsSlice';
import packagesReducer, {
  updateUploadingPackage,
  setUploadQueueState,
  UploadClientStatus,
  wasRecentlyResumed,
  clearRecentResume,
  __resetPackagesModuleStateOnSignOut,
  hydratePackageFromBackend,
} from './packagesSlice';
import { abortAll as abortAllUploads } from '@/Service/ChunkedUploadService';
import { adoptOrphanRecord, requestRehydration } from '@/utils/uploadPersistence';
import { getCurrentOwnerUid } from '@/utils/currentOwnerUid';
import { clearUploadSessionIdOnSignOut } from '@/utils/uploadSessionId';
import clipsReducer from './clipsSlice';
import systemAlertReducer from './systemAlertSlice';
import systemSettingsReducer from './systemSettingsSlice';
import tutorialsReducer from './tutorialsSlice';
import {
  broadcastUploadUpdate,
  isReceivingCrossTabBroadcast,
  subscribeToUploadUpdates,
} from '@/utils/crossTabUploadSync';
import { subscribeUploadQueue } from '@/Service/uploadQueueEvents';
import { subscribeAutoPause } from '@/Service/autoPauseEvents';

// Middleware: mirror every packages/updateUploadingPackage that originated in this tab
// to sibling tabs via BroadcastChannel. Broadcasts are gated inside
// broadcastUploadUpdate on ownership + not-currently-receiving, so echoes can't loop.
const crossTabUploadBroadcastMiddleware = () => (next) => (action) => {
  if (action?.type === 'packages/updateUploadingPackage'
      && !isReceivingCrossTabBroadcast()) {
    broadcastUploadUpdate(action.payload);
  }
  return next(action);
};

// Multi-tenancy sign-out teardown (2026-08-05 leak fix). Fires the imperative
// cleanup that lives OUTSIDE Redux (in-memory tus sessions + module-scoped
// resume tracker) BEFORE the packages reducer's own signOut case runs, so
// any final progress dispatch from an aborting tus.Upload lands into the
// still-populated slice (which is then reset), not into the fresh state.
const signOutTeardownMiddleware = () => (next) => (action) => {
  if (action?.type === 'auth/signOut/fulfilled') {
    try { abortAllUploads(); } catch (_) { /* teardown is best-effort */ }
    try { __resetPackagesModuleStateOnSignOut(); } catch (_) { /* ignore */ }
    // Cross-user sessionId leak fix (2026-08-06). localStorage-persisted
    // sessionId would otherwise carry the outgoing user's identity into the
    // next login on the same browser; the backend's session_id-scoped
    // /sessions/active path would then return the outgoing user's uploads
    // to whoever logs in next.
    try { clearUploadSessionIdOnSignOut(); } catch (_) { /* ignore */ }
  }
  return next(action);
};

// Backend-verified orphan adoption (2026-08-05 follow-up fix). When
// fetchActiveSessions returns the current user's active uploads, any
// IndexedDB record for those same uploadIds that pre-dates the ownerUid
// migration is safely adopted for the current user — the backend has
// already vouched for ownership via the auth token, so no other user can
// claim these records here. Only stamps orphans (adoptOrphanRecord is a
// no-op when a record already has an ownerUid), so a scoped record for
// user B can't be silently reassigned to user A.
//
// Fires AFTER next(action) so the fulfilled reducer's items are already in
// Redux and any component that just re-rendered on the fetch response can
// re-render again on the rehydration event.
const orphanAdoptionMiddleware = () => (next) => (action) => {
  const result = next(action);
  if (action?.type === 'packages/fetchActiveSessions/fulfilled') {
    const currentUid = getCurrentOwnerUid();
    const uploads = action.payload?.uploads;
    if (currentUid && Array.isArray(uploads) && uploads.length > 0) {
      Promise.all(uploads.map((u) =>
        u?.uploadId ? adoptOrphanRecord(u.uploadId, currentUid) : Promise.resolve(false)
      ))
        .then((results) => {
          if (results.some(Boolean)) {
            // At least one orphan was adopted — trigger a rehydration
            // rescan so ResumePreviousUploadBanner surfaces the newly-
            // visible records without waiting for the next visibility-
            // wake or user reload.
            requestRehydration();
          }
        })
        .catch(() => { /* adoption is best-effort; hidden records stay hidden */ });
    }
  }
  return result;
};

// Upgrade backend-only synthesized cards (2026-08-05 follow-up). The packages
// slice's fetchActiveSessions.fulfilled reducer inserts a lightweight PAUSED
// card marked `fromBackendOnly: true` for uploads the current session owns
// but has no IDB record for. This middleware then fires hydratePackageFromBackend
// for each — that thunk fetches the full files manifest, writes it to IDB,
// and replaces the synthesized card with a fully-rehydrated one that can be
// resumed via the normal re-pick + tus flow.
//
// hydrationsInFlight prevents re-dispatching for the same uploadId while a
// previous hydrate is pending (2s cross-device poll would otherwise trigger
// several in parallel).
const hydrationsInFlight = new Set();
const upgradeBackendOnlyCardsMiddleware = (storeApi) => (next) => (action) => {
  const result = next(action);
  if (action?.type === 'packages/fetchActiveSessions/fulfilled') {
    const state = storeApi.getState();
    const synthesized = (state.packages?.uploadingPackages || [])
      .filter((u) => u?.fromBackendOnly && u?.uploadId);
    for (const card of synthesized) {
      if (hydrationsInFlight.has(card.uploadId)) continue;
      hydrationsInFlight.add(card.uploadId);
      Promise.resolve(storeApi.dispatch(hydratePackageFromBackend(card.uploadId)))
        .finally(() => { hydrationsInFlight.delete(card.uploadId); });
    }
  }
  return result;
};

export const store = configureStore({
  reducer: {
    contributors: contributorsReducer,
    mobileMenu: mobileMenuReducer,
    auth: authReducer,
    alert: alertReducer,
    loading: loadingReducer,
    stats: statsReducer,
    packages: packagesReducer,
    clips: clipsReducer,
    systemAlert: systemAlertReducer,
    systemSettings: systemSettingsReducer,
    tutorials: tutorialsReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      crossTabUploadBroadcastMiddleware,
      signOutTeardownMiddleware,
      orphanAdoptionMiddleware,
      upgradeBackendOnlyCardsMiddleware,
    ),
});

// Receiver side: dispatch incoming broadcasts locally. The subscription's own
// dispatches are wrapped in the `receiving` guard inside subscribeToUploadUpdates,
// so the middleware above sees isReceivingCrossTabBroadcast()===true and skips
// re-broadcasting.
subscribeToUploadUpdates((payload) => {
  if (!payload || typeof payload !== 'object' || !payload.uploadId) return;
  store.dispatch(updateUploadingPackage(payload));
});

// Bridge ChunkedUploadService's queue state into Redux (bug 2026-08-05).
// Emitted from acquireUploadSlot / releaseUploadSlot whenever the singleton
// activePackageId / queueWaiters change. Lets UploadStateControl render
// queued uploads with the hourglass icon reliably — decoupled from the
// telemetry.status → PAUSED overwrites that used to hide the queued state.
subscribeUploadQueue((snapshot) => {
  store.dispatch(setUploadQueueState(snapshot));
});

// Bridge auto-pause events (fired by ChunkedUploadService's transient tus
// onError branch) into Redux. Two responsibilities:
//   1. Dispatch PAUSED with pauseReason='AUTO' so the card reflects the
//      transient failure without going terminal FAILED.
//   2. Detect the "immediately after a user Resume click" pattern —
//      symptom of a wake-from-long-sleep stale-session scenario where
//      tus.Upload's in-memory state is corrupt and .start() fires but
//      immediately fails. Rather than let the user click Resume forever,
//      surface a "please refresh the page" hint (2026-08-05).
subscribeAutoPause(({ uploadId, reason, message }) => {
  if (!uploadId) return;
  store.dispatch(updateUploadingPackage({
    uploadId,
    status: UploadClientStatus.PAUSED,
    pauseReason: 'AUTO',
    pausedAt: Date.now(),
    statusMessage: null,
  }));

  if (wasRecentlyResumed(uploadId)) {
    // Clear the marker so we only alert ONCE per resume attempt — otherwise
    // every subsequent auto-pause tick would spam the toast.
    clearRecentResume(uploadId);
    // eslint-disable-next-line no-console
    console.warn('[UPLOAD-DIAG] resume-immediately-repaused', {
      uploadId,
      reason,
      message,
    });
    store.dispatch(showAlert(
      'warning',
      "The upload paused itself again right after resuming — the connection still looks unstable. It will retry on its own when the network recovers, or click Resume to try again."
    ));
  }
});
